import { type ScenarioOrder } from '../../../lib/planningData';
import {
  isLoadEventTimeData,
  type LoadEvent,
  type TimeData,
} from '../../core/TimeData';
import { type PnlData } from '../../hooks/usePnlData';
import { getFreightCost } from '../../shipment/costs/freightCost';
import type { FreightOverrideByOrder } from '../../shipment/income/income';
import type { FreightByOrderId } from '../../shipment/income/orderFreight';
import {
  getFreightByLoadedAmount,
  getFreightsByOrderId,
} from '../../shipment/income/orderFreight';
import { type PnlProcessorContext } from '../context/context';
import { PnlProcessor, type PnlProcessorResult } from '../processor';
import type {
  FreightCost,
  FreightIncome,
  VoyageCharterLumpsumCost,
  VoyageCharterLumpsumIncome,
} from './freightPnl';
import { getTimeDataEventShipmentId } from '../costSharing/costSharing';
import { convertMonetaryValue } from '../../../state/zustand';

type FreightProcessorResult = PnlProcessorResult<
  FreightCost | VoyageCharterLumpsumCost,
  FreightIncome | VoyageCharterLumpsumIncome
>;

const PROCESSOR_METADATA = {
  id: 'freight',
  dependencies: ['cost-sharing'],
};

export interface FreightProcessorInput {
  /** An empty freight item is created for all orders that have no freight associated */
  legacy_addMissingFreights?: boolean;
  orderById: Map<number, ScenarioOrder>;
  overrides: FreightProcessorOverrides;
  pnlData: PnlData;
}

export interface FreightProcessorOverrides {
  /** Override values for incoming order freights */
  incomingFreightOverrideByOrder: FreightOverrideByOrder | null;
}

export class FreightProcessor extends PnlProcessor<
  FreightCost,
  FreightIncome,
  FreightProcessorResult
> {
  private result: FreightProcessorResult = {
    costs: [],
    errors: [],
    income: [],
  };

  private input: FreightProcessorInput;

  private state: {
    freightsByOrderId: {
      incoming: FreightByOrderId;
      outgoing: FreightByOrderId;
    };
    processedLumpsumVoyageCharterIds: Set<number>;
    voyageChartersWithLumpsum: Set<number>;
  };

  constructor(input: FreightProcessorInput, context: PnlProcessorContext) {
    super(PROCESSOR_METADATA, context);
    this.input = input;

    // TODO: maybe this could be optimized by not preloading all freights but
    // only calculating when we encounter a load event
    // this way we skip orders that are not part of the scenario
    const incomingFreightsByOrderId = getFreightsByOrderId(
      input.pnlData.incomingVoyageChartersById,
      input.orderById,
      {
        // TODO:
        // I do not think adding the missing freights this way is a good idea
        // as it's more of an UI concern to render a row for orders that are
        // missing freights but I do not want to break this for now
        addMissingFreights: input.legacy_addMissingFreights ?? false,
      },
    );

    const outgoingFreightsByOrderId = getFreightsByOrderId(
      input.pnlData.outgoingVoyageChartersById,
      input.orderById,
      {
        addMissingFreights: input.legacy_addMissingFreights ?? false,
      },
    );

    this.state = {
      freightsByOrderId: {
        incoming: incomingFreightsByOrderId,
        outgoing: outgoingFreightsByOrderId,
      },
      processedLumpsumVoyageCharterIds: new Set(),
      voyageChartersWithLumpsum: new Set(),
    };
  }

  public processEvent(td: TimeData) {
    // Calculate lumpsum before freights to ensure that freights are ignored
    // if voyage charter has lumpsum present
    this.calculateVoyageCharterLumpsum(td);
    this.calculateFreights(td);
  }

  private calculateFreights(td: TimeData): void {
    if (isLoadEventTimeData(td)) {
      const shipmentId = getTimeDataEventShipmentId(this.context, td);
      const orderId = td.event.order.id;
      const { incomingVoyageCharterId, outgoingVoyageCharterId } =
        this.getLoadEventVoyageCharterIds(td);

      // Skip freight calculation for a voyage charter if it has lumpsum defined
      const skipIncomingFreight =
        incomingVoyageCharterId !== null &&
        this.state.voyageChartersWithLumpsum.has(incomingVoyageCharterId);
      const skipOutgoingFreight =
        outgoingVoyageCharterId !== null &&
        this.state.voyageChartersWithLumpsum.has(outgoingVoyageCharterId);

      const loadedAmount = {
        cargoAmount: +td.event.order.cargoAmount,
        cargoUnit: td.event.order.cargoUnitByCargoUnitId?.name ?? null,
        loadedMass: td.mass,
        loadedVolume: td.volume,
        loadedPcs: td.pcs,
        orderId,
      };

      // TODO:
      // I think this could be more efficient by leveragig the indexed coa
      // prices in the future but let's not break old behavior for now
      const incomingFreight = getFreightByLoadedAmount(
        this.state.freightsByOrderId.incoming,
        loadedAmount,
      );

      const incomingFreigthOverride =
        this.input.overrides.incomingFreightOverrideByOrder?.[orderId];

      const outgoingFreight = getFreightByLoadedAmount(
        this.state.freightsByOrderId.outgoing,
        loadedAmount,
      );

      if (
        !skipIncomingFreight &&
        (incomingFreight || incomingFreigthOverride)
      ) {
        const freightRate =
          incomingFreigthOverride?.freight ?? incomingFreight!.price;
        const freightUnit =
          incomingFreigthOverride?.freightUnit ?? incomingFreight!.unit;

        const freightAmount = getFreightCost(
          loadedAmount.loadedMass,
          loadedAmount.loadedVolume,
          loadedAmount.loadedPcs,
          freightRate,
          freightUnit,
          this.input.pnlData.worldscaleFreightRates,
          td.event.order,
        );

        if (freightAmount) {
          const convertedAmount = convertMonetaryValue(
            freightAmount,
            incomingFreight?.currency ?? this.context.currency.defaultCurrency,
            this.context.currency.rates,
          );

          this.result.income.push({
            amount: convertedAmount,
            category: 'freight_income',
            freightUnit,
            portCallId: td.event.portCallId,
            shipmentId,
            type: 'income',
            source: incomingFreigthOverride
              ? 'override'
              : incomingFreight!.coaAssortmentMatchedBy === null
                ? 'voyage_charter'
                : 'coa',
          });
        }
      }

      if (!skipOutgoingFreight && outgoingFreight) {
        const freightAmount = getFreightCost(
          loadedAmount.loadedMass,
          loadedAmount.loadedVolume,
          loadedAmount.loadedPcs,
          outgoingFreight.price,
          outgoingFreight.unit,
          this.input.pnlData.worldscaleFreightRates,
          td.event.order,
        );

        if (freightAmount) {
          const convertedAmount = convertMonetaryValue(
            freightAmount,
            outgoingFreight?.currency ?? this.context.currency.defaultCurrency,
            this.context.currency.rates,
          );

          this.result.costs.push({
            amount: convertedAmount,
            category: 'freight_cost',
            freightUnit: outgoingFreight.unit,
            portCallId: td.event.portCallId,
            shipmentId,
            type: 'cost',
            source:
              outgoingFreight.coaAssortmentMatchedBy === null
                ? 'voyage_charter'
                : 'coa',
          });
        }
      }
    }
  }

  private calculateVoyageCharterLumpsum(td: TimeData): void {
    if (isLoadEventTimeData(td)) {
      const shipmentId = getTimeDataEventShipmentId(this.context, td);

      const { incomingVoyageCharterId, outgoingVoyageCharterId } =
        this.getLoadEventVoyageCharterIds(td);

      if (
        incomingVoyageCharterId &&
        !this.state.processedLumpsumVoyageCharterIds.has(
          incomingVoyageCharterId,
        )
      ) {
        const incomingVoyageCharter =
          this.input.pnlData.incomingVoyageChartersById.get(
            incomingVoyageCharterId,
          );

        if (incomingVoyageCharter?.lumpsum) {
          const lumpsumAmount = convertMonetaryValue(
            Number(incomingVoyageCharter.lumpsum),
            incomingVoyageCharter.currency ??
              this.context.currency.defaultCurrency,
            this.context.currency.rates,
          );

          this.result.income.push({
            type: 'income',
            category: 'voyage_charter_lumpsum_income',
            amount: lumpsumAmount,
            portCallId: td.event.portCallId,
            shipmentId,
          });

          this.state.voyageChartersWithLumpsum.add(incomingVoyageCharterId);
        }

        this.state.processedLumpsumVoyageCharterIds.add(
          incomingVoyageCharterId,
        );
      }

      if (
        outgoingVoyageCharterId &&
        !this.state.processedLumpsumVoyageCharterIds.has(
          outgoingVoyageCharterId,
        )
      ) {
        const outgoingVoyageCharter =
          this.input.pnlData.outgoingVoyageChartersById.get(
            outgoingVoyageCharterId,
          );

        if (outgoingVoyageCharter?.lumpsum) {
          const lumpsumAmount = convertMonetaryValue(
            Number(outgoingVoyageCharter.lumpsum),
            outgoingVoyageCharter.currency ??
              this.context.currency.defaultCurrency,
            this.context.currency.rates,
          );

          this.result.costs.push({
            type: 'cost',
            category: 'voyage_charter_lumpsum_cost',
            amount: lumpsumAmount,
            portCallId: td.event.portCallId,
            shipmentId,
          });

          this.state.voyageChartersWithLumpsum.add(outgoingVoyageCharterId);
        }

        this.state.processedLumpsumVoyageCharterIds.add(
          outgoingVoyageCharterId,
        );
      }
    }
  }

  private getLoadEventVoyageCharterIds(td: TimeData<LoadEvent>) {
    const order = this.input.orderById.get(td.event.order.id);

    if (!order) {
      this.result.errors.push({
        error: `Order not found for load event by order id ${td.event.order.id}`,
        processor: PROCESSOR_METADATA.id,
      });
    }

    return {
      incomingVoyageCharterId:
        order?.incomingVoyageCharterRelation?.voyageCharterId ?? null,
      outgoingVoyageCharterId:
        order?.outgoingVoyageCharterRelation?.voyageCharterId ?? null,
    };
  }

  public finalize() {}
  public getResult() {
    return this.result;
  }
}
